/**
 * Hit again › Amount: one select (the same amount · + % of the drop · + % of
 * the base · a fixed amount) and, only when the mode needs one, its number
 * with its unit. A fixed amount starts at the container's amount, so the
 * field and the engine agree.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ChevronDown, Coins } from 'lucide-react';
import { DROP_GEN_HIT_AMOUNT_MODES, DROP_GEN_HIT_LIMITS, type DropGenHitAmountMode } from '@trafficpulse/shared';

import { NumberField } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { DraftHitAgain } from '../../../../../drop-generator.types';
import { HIT_FIELD, HIT_SELECT, HIT_UNIT, HitRow } from './HitGrid';

export const MODE_WORDS: Record<DropGenHitAmountMode, string> = {
  same: 'Same amount',
  drop_pct: '± % of the drop',
  base_pct: '± % of the base',
  fixed: 'Fixed amount',
};

const HINT =
  'How much the new drop sends. Same amount = what the drop that hit sent; ± % of the drop adds (or, below zero, takes off) X % of what it sent (and grows hit after hit when the chain repeats); ± % of the base does the same on the container’s amount, the same every time; a fixed amount ignores both. It still goes through the list’s capacity and the daily caps.';

const { amount, pct } = DROP_GEN_HIT_LIMITS;

interface Props {
  hitAgain: DraftHitAgain;
  defaultAmount: number;
  onPatch: (patch: Partial<DraftHitAgain>) => void;
}

const Value: FC<Props> = ({ hitAgain, defaultAmount, onPatch }) => {
  if (hitAgain.mode === 'same') return null;
  if (hitAgain.mode === 'fixed')
    return (
      <span className="inline-flex items-center gap-1.5" data-testid="hit-fixed">
        <NumberField value={hitAgain.fixed ?? defaultAmount} onChange={(v) => onPatch({ fixed: v })} min={amount.min} max={amount.max} className={cn(HIT_FIELD, 'w-28')} aria-label="Fixed amount of the hit-again drop" />
        <span className={HIT_UNIT}>SMS</span>
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="hit-pct">
      <NumberField value={hitAgain.pct} onChange={(v) => onPatch({ pct: v })} min={pct.min} max={pct.max} className={cn(HIT_FIELD, 'w-20')} aria-label="Percent of the drop or the base added to the hit-again drop" />
      <span className={HIT_UNIT}>%</span>
    </span>
  );
};

export const HitAmount: FC<Props> = (props) => {
  const { hitAgain, defaultAmount, onPatch } = props;
  const pick = (mode: DropGenHitAmountMode) =>
    onPatch(mode === 'fixed' && hitAgain.fixed === null ? { mode, fixed: Math.max(amount.min, defaultAmount) } : { mode });
  return (
    <HitRow icon={Coins} label="Amount" hint={HINT} testId="hit-amount">
      <span className="relative inline-flex">
        <select value={hitAgain.mode} onChange={(e) => pick(e.target.value as DropGenHitAmountMode)} aria-label="How the hit-again drop's amount is made" className={cn(HIT_SELECT, 'w-44')}>
          {DROP_GEN_HIT_AMOUNT_MODES.map((mode) => (
            <option key={mode} value={mode}>
              {MODE_WORDS[mode]}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      </span>
      <Value {...props} />
    </HitRow>
  );
};
